import { useEffect, useState } from "react";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { Plus, X, Trash2, Eye, Sparkles, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import Markdown from "../components/Markdown";

const Competition = () => {
  const [accounts, setAccounts] = useState([]);
  const [posts, setPosts] = useState([]);
  const [tab, setTab] = useState("accounts");
  const [showAccount, setShowAccount] = useState(false);
  const [showPost, setShowPost] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/competitor-accounts"), api.get("/competitor-posts")])
      .then(([a, p]) => { setAccounts(a.data); setPosts(p.data); })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  // Sort buckets
  const viral = [...posts].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 8);
  const recent = [...posts].sort((a, b) => (b.saved_at || "").localeCompare(a.saved_at || "")).slice(0, 8);
  const mostComm = [...posts].sort((a, b) => (b.comments || 0) - (a.comments || 0)).slice(0, 8);

  return (
    <Layout
      title="Radar de Competencia e Inspiración"
      subtitle="Detecta patrones, no copies"
      actions={
        <button onClick={() => tab === "accounts" ? setShowAccount(true) : setShowPost(true)} className="btn-primary flex items-center gap-2">
          <Plus size={14} /> {tab === "accounts" ? "Nueva cuenta" : "Nueva referencia"}
        </button>
      }
    >
      <div className="flex gap-2 mb-5 border-b border-[#E5E5E5] overflow-x-auto">
        <button onClick={() => setTab("accounts")} data-testid="tab-accounts" className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${tab === "accounts" ? "border-[#D99D26] text-[#0A0A0A]" : "border-transparent text-[#666]"}`}>
          Cuentas ({accounts.length})
        </button>
        <button onClick={() => setTab("posts")} data-testid="tab-posts" className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${tab === "posts" ? "border-[#D99D26] text-[#0A0A0A]" : "border-transparent text-[#666]"}`}>
          Posts ({posts.length})
        </button>
        <button onClick={() => setTab("viral")} data-testid="tab-viral" className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${tab === "viral" ? "border-[#D99D26] text-[#0A0A0A]" : "border-transparent text-[#666]"}`}>
          Más virales
        </button>
        <button onClick={() => setTab("recent")} data-testid="tab-recent" className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${tab === "recent" ? "border-[#D99D26] text-[#0A0A0A]" : "border-transparent text-[#666]"}`}>
          Más recientes
        </button>
        <button onClick={() => setTab("most-commented")} data-testid="tab-comm" className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${tab === "most-commented" ? "border-[#D99D26] text-[#0A0A0A]" : "border-transparent text-[#666]"}`}>
          Más comentados
        </button>
        <button onClick={() => setTab("influencer")} data-testid="tab-influencer" className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${tab === "influencer" ? "border-[#D99D26] text-[#0A0A0A]" : "border-transparent text-[#666]"}`}>
          🔭 Descubrimiento influencers
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><span className="spinner" /></div>
      ) : tab === "accounts" ? (
        accounts.length === 0 ? (
          <Empty msg="Sin cuentas registradas" onCreate={() => setShowAccount(true)} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {accounts.map((a) => (
              <div key={a.id} className="aura-card p-5 group relative" data-testid={`account-${a.id}`}>
                <div className="flex justify-between items-start mb-2">
                  <span className={`pill ${a.classification === "competidor" ? "pill-red" : a.classification === "benchmark" ? "pill-dark" : "pill-yellow"}`}>{a.classification}</span>
                  <span className="pill pill-gray text-[10px]">{a.platform}</span>
                </div>
                <h3 className="font-display text-lg font-medium">{a.name}</h3>
                <div className="text-sm text-[#8a5e10] font-mono">{a.handle}</div>
                {a.followers > 0 && <div className="text-xs text-[#666] mt-1">{a.followers.toLocaleString()} seguidores</div>}
                {a.notes && <p className="text-xs text-[#666] mt-2 line-clamp-2">{a.notes}</p>}
                <div className="flex gap-2 mt-3">
                  {a.url && <a href={a.url} target="_blank" rel="noopener noreferrer" className="btn-ghost text-xs flex items-center gap-1"><ExternalLink size={11} /> Visitar</a>}
                  <button onClick={async () => {
                    if (!window.confirm("¿Eliminar?")) return;
                    await api.delete(`/competitor-accounts/${a.id}`);
                    load();
                  }} className="opacity-0 group-hover:opacity-100 text-[#666] hover:text-red-600 ml-auto"><Trash2 size={13} /></button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : tab === "influencer" ? (
        <InfluencerDiscoveryComingSoon />
      ) : (
        <PostsGrid posts={tab === "viral" ? viral : tab === "recent" ? recent : tab === "most-commented" ? mostComm : posts} onSelect={setSelectedPost} sortLabel={tab} />
      )}

      {showAccount && <AccountForm onClose={() => setShowAccount(false)} onSaved={() => { setShowAccount(false); load(); }} />}
      {showPost && <PostForm accounts={accounts} onClose={() => setShowPost(false)} onSaved={() => { setShowPost(false); load(); }} />}
      {selectedPost && <PostDetail post={selectedPost} onClose={() => setSelectedPost(null)} onChange={() => { setSelectedPost(null); load(); }} />}
    </Layout>
  );
};

const PostsGrid = ({ posts, onSelect, sortLabel }) => {
  if (posts.length === 0) {
    return <div className="aura-card-flat p-12 text-center text-sm text-[#666]">Sin posts registrados. Agrega una referencia para empezar.</div>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {posts.map((p, idx) => (
        <div key={p.id} onClick={() => onSelect(p)} className="aura-card p-5 cursor-pointer" data-testid={`comp-post-${p.id}`}>
          <div className="flex justify-between items-start mb-2">
            <div className="flex items-center gap-2">
              {sortLabel !== "posts" && <span className="font-display text-lg text-[#D99D26]">#{idx + 1}</span>}
              <span className="pill pill-gray text-[10px]">{p.format}</span>
            </div>
            <a href={p.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-[#666] hover:text-[#0A0A0A]"><ExternalLink size={13} /></a>
          </div>
          <h3 className="font-display text-base font-medium leading-tight line-clamp-2">{p.title || "Sin título"}</h3>
          <div className="text-xs text-[#666] mt-1">{p.account_name}</div>
          <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
            <div className="text-center"><div className="font-medium">{(p.views || 0).toLocaleString()}</div><div className="text-[#666] text-[10px]">Vistas</div></div>
            <div className="text-center"><div className="font-medium">{p.likes || 0}</div><div className="text-[#666] text-[10px]">Likes</div></div>
            <div className="text-center"><div className="font-medium">{p.comments || 0}</div><div className="text-[#666] text-[10px]">Coment.</div></div>
          </div>
          {p.adapted_idea && <div className="mt-2 text-[10px] text-green-700 flex items-center gap-1"><Sparkles size={10} /> Insight Aura</div>}
        </div>
      ))}
    </div>
  );
};

const InfluencerDiscoveryComingSoon = () => (
  <div className="aura-card-flat p-12 text-center max-w-2xl mx-auto">
    <div className="w-16 h-16 rounded-2xl bg-[#D99D26]/15 text-[#8a5e10] flex items-center justify-center mx-auto mb-5">
      <Eye size={28} />
    </div>
    <span className="pill pill-yellow mb-3 inline-block">🚀 Próximamente</span>
    <h3 className="font-display text-3xl font-medium tracking-tight">Motor de descubrimiento de influencers</h3>
    <p className="text-[#666] mt-3 max-w-md mx-auto leading-relaxed">
      Búsqueda avanzada en Instagram y TikTok por nicho de perfumería, beauty, lifestyle. Filtros por
      seguidores (1k-1M+), engagement (3%+), ubicación, idioma, frecuencia de publicación y costo estimado.
    </p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-6 text-left text-sm">
      <div className="aura-card-flat p-4 bg-[#F0F0F0]">
        <div className="label-overline mb-1">Opción A · API Apify</div>
        <div className="text-[#444]">Scraping legal Instagram + TikTok. ~$10-30 USD/mes según volumen. Setup en horas.</div>
      </div>
      <div className="aura-card-flat p-4 bg-[#F0F0F0]">
        <div className="label-overline mb-1">Opción B · Modash / Heepsy</div>
        <div className="text-[#444]">Plataformas dedicadas a influencer marketing. ~$120-500 USD/mes. Datos enriquecidos.</div>
      </div>
    </div>
    <p className="text-xs text-[#666] mt-5">
      Avísame cuando quieras activarlo. Mientras tanto, agrega cuentas manualmente y Aura enriquece cada post con IA.
    </p>
  </div>
);

const Empty = ({ msg, onCreate }) => (
  <div className="aura-card-flat p-12 text-center">
    <div className="w-14 h-14 rounded-2xl bg-[#D99D26]/15 text-[#8a5e10] flex items-center justify-center mx-auto mb-4">
      <Eye size={24} />
    </div>
    <h3 className="font-display text-2xl font-medium">{msg}</h3>
    <p className="text-[#666] mt-2 mb-5">Aura aprenderá de la competencia para adaptar ideas a Rubí.</p>
    <button onClick={onCreate} className="btn-primary">Agregar primero</button>
  </div>
);

const AccountForm = ({ onClose, onSaved }) => {
  const [form, setForm] = useState({ name: "", handle: "", platform: "instagram", url: "", classification: "competidor", notes: "", followers: 0 });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    await api.post("/competitor-accounts", { ...form, followers: parseInt(form.followers) || 0 });
    toast.success("Cuenta agregada");
    onSaved();
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl w-full max-w-md p-7 space-y-4">
        <div className="flex justify-between items-start"><div><div className="label-overline">Agregar</div><h2 className="font-display text-2xl font-medium">Cuenta a monitorear</h2></div><button type="button" onClick={onClose}><X size={18} /></button></div>
        <div><label className="aura-label">Nombre *</label><input required className="aura-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="account-name" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="aura-label">Handle</label><input className="aura-input" value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value })} placeholder="@cuenta" /></div>
          <div><label className="aura-label">Plataforma</label><select className="aura-input" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })}>{["instagram", "tiktok", "facebook", "youtube"].map((p) => <option key={p}>{p}</option>)}</select></div>
          <div><label className="aura-label">URL</label><input className="aura-input" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} /></div>
          <div><label className="aura-label">Seguidores</label><input type="number" className="aura-input" value={form.followers} onChange={(e) => setForm({ ...form, followers: e.target.value })} /></div>
          <div className="col-span-2"><label className="aura-label">Clasificación</label><select className="aura-input" value={form.classification} onChange={(e) => setForm({ ...form, classification: e.target.value })}>{["competidor", "inspiracion", "benchmark", "creador", "influencer", "marca_internacional"].map((c) => <option key={c}>{c}</option>)}</select></div>
          <div className="col-span-2"><label className="aura-label">Notas</label><textarea className="aura-input" rows="2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        </div>
        <button type="submit" className="btn-primary w-full justify-center">Agregar cuenta</button>
      </form>
    </div>
  );
};

const PostForm = ({ accounts, onClose, onSaved }) => {
  const [form, setForm] = useState({
    account_id: "", url: "", thumbnail_url: "", title: "", format: "reel", theme: "", hook: "",
    views: 0, likes: 0, comments: 0, saves: 0, shares: 0, learnings: "",
  });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.url.trim()) return;
    const acc = accounts.find((a) => a.id === form.account_id);
    await api.post("/competitor-posts", {
      ...form,
      account_name: acc?.name || "",
      views: parseInt(form.views) || 0,
      likes: parseInt(form.likes) || 0,
      comments: parseInt(form.comments) || 0,
      saves: parseInt(form.saves) || 0,
      shares: parseInt(form.shares) || 0,
    });
    toast.success("Referencia guardada");
    onSaved();
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto p-7 space-y-4">
        <div className="flex justify-between items-start"><div><div className="label-overline">Referencia viral</div><h2 className="font-display text-2xl font-medium">Nueva referencia</h2></div><button type="button" onClick={onClose}><X size={18} /></button></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><label className="aura-label">URL *</label><input required className="aura-input" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://..." /></div>
          <div className="col-span-2"><label className="aura-label">Título</label><input className="aura-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
          <div><label className="aura-label">Cuenta</label><select className="aura-input" value={form.account_id} onChange={(e) => setForm({ ...form, account_id: e.target.value })}><option value="">— —</option>{accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></div>
          <div><label className="aura-label">Formato</label><select className="aura-input" value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })}>{["reel", "post", "tiktok", "story", "video", "carrusel"].map((f) => <option key={f}>{f}</option>)}</select></div>
          <div><label className="aura-label">Tema</label><input className="aura-input" value={form.theme} onChange={(e) => setForm({ ...form, theme: e.target.value })} /></div>
          <div><label className="aura-label">Hook</label><input className="aura-input" value={form.hook} onChange={(e) => setForm({ ...form, hook: e.target.value })} /></div>
          <div><label className="aura-label">Vistas</label><input type="number" className="aura-input" value={form.views} onChange={(e) => setForm({ ...form, views: e.target.value })} /></div>
          <div><label className="aura-label">Likes</label><input type="number" className="aura-input" value={form.likes} onChange={(e) => setForm({ ...form, likes: e.target.value })} /></div>
          <div><label className="aura-label">Comentarios</label><input type="number" className="aura-input" value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} /></div>
          <div><label className="aura-label">Guardados</label><input type="number" className="aura-input" value={form.saves} onChange={(e) => setForm({ ...form, saves: e.target.value })} /></div>
          <div className="col-span-2"><label className="aura-label">Aprendizajes</label><textarea className="aura-input" rows="2" value={form.learnings} onChange={(e) => setForm({ ...form, learnings: e.target.value })} placeholder="¿Qué hace que esto funcione?" /></div>
        </div>
        <button type="submit" className="btn-primary w-full justify-center">Guardar referencia</button>
      </form>
    </div>
  );
};

const PostDetail = ({ post, onClose, onChange }) => {
  const [adapted, setAdapted] = useState(post.adapted_idea || "");
  const [generating, setGenerating] = useState(false);
  const adapt = async () => {
    setGenerating(true);
    try {
      const res = await api.post("/aura/enrich-competitor-post", {
        url: post.url,
        title: post.title,
        account_name: post.account_name,
        platform: post.platform || "instagram",
        format: post.format,
        views: post.views,
        likes: post.likes,
        comments: post.comments,
        saves: post.saves,
      });
      setAdapted(res.data.insights);
      await api.patch(`/competitor-posts/${post.id}`, { adapted_idea: res.data.insights });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setGenerating(false); }
  };
  const remove = async () => {
    if (!window.confirm("¿Eliminar?")) return;
    await api.delete(`/competitor-posts/${post.id}`);
    onChange();
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-7">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="label-overline">{post.account_name}</div>
            <h2 className="font-display text-2xl font-medium">{post.title || "Referencia"}</h2>
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <a href={post.url} target="_blank" rel="noopener noreferrer" className="btn-outline text-sm inline-block mb-4">Ver original</a>
        {post.learnings && <div className="mb-4"><div className="label-overline">Aprendizaje</div><p className="text-sm mt-1">{post.learnings}</p></div>}
        <button onClick={adapt} disabled={generating} className="btn-primary flex items-center gap-2 mb-4" data-testid="adapt-btn">
          {generating ? <><span className="spinner" /> Aura analizando...</> : <><Sparkles size={14} /> Enriquecer con Aura · Insights</>}
        </button>
        {adapted && (
          <div className="aura-card-flat p-5 max-h-80 overflow-y-auto" data-testid="adapted-content">
            <Markdown content={adapted} />
          </div>
        )}
        <div className="flex justify-end mt-4">
          <button onClick={remove} className="btn-ghost text-red-600"><Trash2 size={14} className="inline mr-1" /> Eliminar</button>
        </div>
      </div>
    </div>
  );
};

export default Competition;
